import { FC } from 'react';

import { Card } from '@mui/material';
import { VITE_AZURE_AUTH_TENANT_ID } from '@src/main/env';
import { useStatisticsContext } from '@src/main/ui/providers/statistics/StatisticsContextProvider';
import { useUserContext } from '@src/main/ui/providers/user/UserContextProvider';

import CardAction from '@src/main/ui/components/common/CardAction/CardAction';
import DashboardCardResumeButtons from '@src/main/ui/components/common/Dashboard/DashboardCardResume/DashboardCardResumeButtons/DashboardCardResumeButtons.module';
import DashboardCardResumeContent from '@src/main/ui/components/common/Dashboard/DashboardCardResume/DashboardCardResumeContent/DashboardCardResumeContent';
import DashboardCardResumeFooter from '@src/main/ui/components/common/Dashboard/DashboardCardResume/DashboardCardResumeFooter/DashboardCardResumeFooter';
import DashboardCardResumeHeader from '@src/main/ui/components/common/Dashboard/DashboardCardResume/DashboardCardResumeHeader/DashboardCardResumeHeader';
import { HighlighFields } from '@src/main/ui/components/common/Dashboard/common/fields/highlighter/HighlighFields';
import { IconId } from '../../Icon/IconId';
import styles from './DashboardCardResume.module.scss';

interface DashboardCardResumeProps {
  id: string;
  name: string;
  icon: IconId;
  artifactId: string;
  workspaceId: string | undefined;
  artifactType: string;
  areas: string[];
  keyTags: string[];
  tags: string[];
  description?: string;
  isCertified: boolean;
  isShowAll: boolean;
  refreshDate?: Date;
  views?: number;
  highlighFields?: HighlighFields[];
  actions: {
    edit?: boolean;
    delete?: boolean;
    favorite?: boolean;
    info?: boolean;
  };
  haveAccess: boolean;
  repositoryType: string | null;
  allEmployees: boolean;
  onClickFavorites: () => void;
  onClickShowMoreInfo: () => void;
  onClickEdit: () => void;
  onClickDelete: () => void;
}

const defaultProps = {
  description: '',
  refreshDate: undefined,
  views: undefined,
  highlighFields: undefined,
};

const DashboardCardResume: FC<DashboardCardResumeProps> = ({
  id,
  name,
  icon,
  artifactId,
  workspaceId,
  artifactType,
  areas,
  keyTags,
  tags,
  description,
  isCertified,
  isShowAll,
  refreshDate,
  views,
  highlighFields,
  actions,
  haveAccess,
  repositoryType,
  allEmployees,
  onClickFavorites,
  onClickShowMoreInfo,
  onClickEdit,
  onClickDelete,
}) => {
  const { user } = useUserContext();
  const { createReportStatistics } = useStatisticsContext();
  const tenantId = VITE_AZURE_AUTH_TENANT_ID;

  const onCreateReport = () => {
    if (user) {
      const report = {
        userId: user?.id,
        userName: user?.name,
        reportName: name,
      };
      createReportStatistics(report);
    }
  };
  return (
    <Card
      className={`${styles.dashboardCardResume} ${
        haveAccess ||
        allEmployees ||
        artifactType === 'PowerApp' ||
        artifactType === 'PowerBIRepository'
          ? ''
          : styles.dashboardCardResume__notHaveAccess
      }`}
    >
      <CardAction
        launchUrl={
          artifactType === 'PowerBIRepository'
            ? // eslint-disable-next-line max-len
              `https://app.powerbi.com/groups/me/apps/${workspaceId}/${repositoryType}/${artifactId}?ctid=${tenantId}&portalSessionId=2cf4a096-10d2-4b86-b154-e60887553c41&experience=power-bi`
            : `/dashboard/${id}`
        }
        target={artifactType === 'PowerBIRepository'}
        onCreateReport={onCreateReport}
      >
        <DashboardCardResumeHeader
          title={name}
          icon={icon}
          isCertified={isCertified}
          isShowAll={isShowAll}
          highlighFields={highlighFields}
        />
        <DashboardCardResumeContent
          areas={areas}
          keyTags={keyTags}
          tags={tags}
          description={description}
          highlighFields={highlighFields}
        />
        <DashboardCardResumeFooter refreshDate={refreshDate} views={views} />
      </CardAction>
      <DashboardCardResumeButtons
        isFavorite={!!user?.favorites.includes(id)}
        actions={actions}
        onClickEdit={onClickEdit}
        onClickDelete={onClickDelete}
        onClickFavorites={onClickFavorites}
        onClickShowMoreInfo={onClickShowMoreInfo}
      />
    </Card>
  );
};

DashboardCardResume.defaultProps = defaultProps;

export default DashboardCardResume;
